// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

// Storage keys
export const STORAGE_KEYS = {
	AUTH_TOKEN: 'bbp_auth_token',
	USER_DATA: 'bbp_user_data',
	REMEMBER_ME: 'bbp_remember_me',
	LOGIN_TIMESTAMP: 'bbp_login_timestamp',
	// sessionStorage (not localStorage): "Remind me Later" defers the personal-info
	// prompt for this session only, so a blocking compliance request resurfaces on
	// the next login rather than being buried indefinitely.
	INFO_REQUEST_DISMISSED: 'bbp_info_request_dismissed',
} as const;

// Session expiration duration (1 hour in milliseconds)
export const SESSION_EXPIRATION_MS = 60 * 60 * 1000; // 1 hour

// API endpoints
export const API_ENDPOINTS = {
	AUTH: {
		SIGNIN: '/user/signin',
		CHANGE_PASSWORD: '/user/change-password',
	},
	OTP: {
		SEND: '/otp/send',
		VERIFY: '/otp/verify',
		RESEND: '/otp/resend',
	},
	DASHBOARD: '/dashboard',
	PROVIDERS: {
		LIST: '/provider',
		CREATE: '/provider',
		DETAIL: (id: number) => `/provider/${id}`,
		UPDATE: (id: number) => `/provider/${id}`,
	},
	PROVIDER: {
		ME: '/provider/me',
		DASHBOARD: '/provider/dashboard',
		PAYMENT_SETUP: (id: number) => `/provider/${id}/payment/bank/setup`,
		PAYMENT_CONFIRM: (id: number) => `/provider/${id}/payment/bank/confirm`,
		PAYMENT_VERIFY: (id: number) => `/provider/${id}/payment/bank/verify`,
		TCR_APPROVALS: '/provider/tcr-approvals',
		TCR_APPROVE: (id: number) => `/provider/tcr/${id}/approve`,
		TCR_REJECT: (id: number) => `/provider/tcr/${id}/reject`,
		TCR_PHOTOS: (id: number) => `/provider/tcr/${id}/photos`,
		TCR_PAYMENTS: '/provider/tcr-payments',
		TCR_PAYMENTS_PAY: '/provider/tcr-payments/pay',
		REVENUE: '/provider/revenue',
	},
	LOCATION: '/location',
	BUSINESS_HOURS: (locationId: number) => `/location/${locationId}/business-hours`,
	// Salon-wide closures: holidays, renovations. Override hours, staff and booking.
	LOCATION_CLOSURES: (locationId: number) => `/location/${locationId}/closures`,
	LOCATION_CLOSURE_DETAIL: (closureId: number) => `/location/closures/${closureId}`,
	// Staff time off. Provider records under BASE; staff self-service under ME.
	TIME_OFF: {
		BASE: '/time-off',
		DETAIL: (id: number) => `/time-off/${id}`,
		APPROVE: (id: number) => `/time-off/${id}/approve`,
		DECLINE: (id: number) => `/time-off/${id}/decline`,
		ME: '/time-off/me',
		ME_PREVIEW: '/time-off/me/preview',
		ME_DETAIL: (id: number) => `/time-off/me/${id}`,
		ME_WITHDRAW: (id: number) => `/time-off/me/${id}/withdraw`,
		RESOLUTION: (id: number) => `/time-off/${id}/resolution`,
		RESOLVE_BOOKING: (id: number, appointmentId: number) =>
			`/time-off/${id}/bookings/${appointmentId}/resolve`,
	},
	SERVICE: {
		BASE: '/service',
		MY_SERVICES: '/service/my-services',
		BULK_UPLOAD: '/service/bulk-upload',
		BULK_UPLOAD_TEMPLATE: '/service/bulk-upload/template',
		BULK_UPLOAD_STATUS: (id: number) => `/service/bulk-upload/${id}/status`,
	},
	SERVICE_CATEGORY: {
		BASE: '/service-category',
		DETAIL: (id: number) => `/service-category/${id}`,
		SERVICES: (id: number) => `/service-category/${id}/services`,
	},
	SERVICE_TASK: '/service-task',
	SERVICE_TASK_PRICING: (id: number) => `/service-task/${id}/pricing`,
	SERVICE_TASK_AVAILABLE: '/service-task/available-services',
	PROVIDER_TASK: {
		BASE: '/provider-task',
		DETAIL: (id: number) => `/provider-task/${id}`,
	},
	SERVICE_TASK_BULK_UPLOAD: '/service-task/bulk-upload',
	SERVICE_TASK_BULK_UPLOAD_TEMPLATE: '/service-task/bulk-upload/template',
	SERVICE_TASK_BULK_UPLOAD_STATUS: (id: number) => `/service-task/bulk-upload/${id}/status`,
	DEFAULT_SERVICE: '/default-service',
	STAFF: {
		BASE: '/staff',
		ME: '/staff/me',
		UPDATE_PERSONAL_INFO: '/staff/me/personal-info',
		UPDATE_SERVICES: '/staff/me/services',
		UPDATE_TASKS: '/staff/me/tasks',
		DASHBOARD: '/staff/dashboard',
		BOOKINGS: '/staff/bookings',
		BOOKING_DETAIL: (id: number) => `/staff/bookings/${id}`,
		TCR: '/staff/tcr',
		TCR_DETAIL: (id: number) => `/staff/tcr/${id}`,
		TCR_PHOTOS: (id: number) => `/staff/tcr/${id}/photos`,
		AGREEMENT: (type: string) => `/staff/me/agreement/${type}`,
		UPDATE_ADDRESS: '/staff/me/address',
		UPDATE_EMERGENCY_CONTACT: '/staff/me/emergency-contact',
		DETAIL: (id: number) => `/staff/${id}`,
		PORTFOLIO: (id: number) => `/staff/${id}/portfolio`,
		SEARCH: '/staff/search',
		BY_TASKS: '/staff/by-tasks',
		SERVICE_TASKS: '/staff/service-tasks',
		// Services at the staff member's assigned location. The location is
		// derived from the token, so no locationId is sent.
		SERVICES: '/staff/service',
		SERVICE_DETAIL: (id: number) => `/staff/service/${id}`,
		PAYMENT_SETUP: '/staff/me/payment/bank/setup',
		EARNINGS: '/staff/earnings',
		// Personal-info request (provider sends, staff completes)
		INFO_REQUEST: (id: number) => `/staff/${id}/info-request`,
		ME_INFO_REQUEST: '/staff/me/info-request',
		// Read-only salon hours + closures at the staff member's locations
		SALON_HOURS: '/staff/me/salon-hours',
	},
	STAFF_NETWORK: {
		// Provider side: rotation staff directory and invitations
		AREAS: '/staff/network/areas',
		SEARCH: '/staff/network/search',
		INVITES: '/staff/network/invites',
		WITHDRAW_INVITE: (id: number) => `/staff/network/invites/${id}/withdraw`,
		ENGAGEMENTS_OF: (staffId: number) => `/staff/network/${staffId}/engagements`,
		// Staff side: my invitations, salons and listing
		MY_INVITES: '/staff/me/invites',
		ACCEPT_INVITE: (id: number) => `/staff/me/invites/${id}/accept`,
		DECLINE_INVITE: (id: number) => `/staff/me/invites/${id}/decline`,
		MY_ENGAGEMENTS: '/staff/me/engagements',
		MY_LISTING: '/staff/me/rotation-listing',
	},
	CUSTOMER: {
		LOOKUP_BY_EMAIL: '/customer/lookup-by-email',
		LOOKUP_BY_PHONE: '/customer/lookup',
		BASE: '/customer',
		DETAIL: (id: number) => `/customer/${id}`,
		DASHBOARD: '/customer/dashboard',
		EXPORT: '/customer/export',
		// CSV import: template download, multipart upload (returns a job), job polling.
		IMPORT: '/customer/import',
		IMPORT_TEMPLATE: '/customer/import/template',
		IMPORT_STATUS: (jobId: number) => `/customer/import/${jobId}/status`,
	},
	APPOINTMENT: {
		BASE: '/appointment',
		DASHBOARD_LIST: '/appointment/dashboard',
		UPDATE_STATUS: (id: number, status: string) => `/appointment/${id}/status/${status}`,
		RESCHEDULE: (id: number) => `/appointment/${id}/reschedule`,
		SCHEDULE: (id: number) => `/appointment/${id}/schedule`,
		SERVICE_DETAILS: (id: number) => `/appointment/${id}/service-details`,
	},
	BOOKING_LINK: {
		GENERATE: '/booking-link/generate',
	},
	// Customer-facing booking link pages. Public: never send a bearer token.
	PUBLIC_BOOKING: {
		INFO: (token: string) => `/api/public-booking/${encodeURIComponent(token)}`,
		AVAILABILITY: (token: string) =>
			`/api/public-booking/${encodeURIComponent(token)}/availability`,
		CREATE: (token: string) => `/api/public-booking/${encodeURIComponent(token)}/create`,
		PAID_INTENT: (token: string) =>
			`/api/public-booking/${encodeURIComponent(token)}/paid/intent`,
		PAID_STATUS: (token: string) =>
			`/api/public-booking/${encodeURIComponent(token)}/paid/status`,
	},
	INTEGRATION: {
		BASE: '/integration',
		DETAIL: (id: number) => `/integration/${id}`,
	},
	INVOICE: {
		LIST: '/invoice/list',
		SEND: '/invoice/send',
		DETAIL: (id: number) => `/invoice/${id}`,
	},
	SETTINGS: {
		ROLES: '/settings/roles',
		ROLE_DETAIL: (id: number) => `/settings/roles/${id}`,
		PERMISSIONS: '/settings/permissions',
	},
	STRIPE_CONNECT: {
		ACCOUNT_SESSION: '/api/stripe/connect/account-session',
		STATUS: '/api/stripe/connect/status',
		SUBSCRIPTION: '/api/stripe/connect/subscription',
		SUBSCRIPTION_PAUSE: '/api/stripe/connect/subscription/pause',
		SUBSCRIPTION_RESUME: '/api/stripe/connect/subscription/resume',
		DISCONNECT_ACCOUNT: '/api/stripe/connect/account',
	},
} as const;
